import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHouse,
  faUsers,
  faIndustry,
  faLayerGroup,
  faTag,
  faCubesStacked,
  faScrewdriverWrench,
  faListCheck,
  faGears,
  faGear,
  faBuilding,
  faClipboardList,
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faChevronUp,
  faSitemap,
  faShieldHalved,
  faUserGroup,
  faShield,
  faFlag,
  faClipboardCheck,
  faBoxOpen,
  faBoxesStacked,
  faBookOpen,
  faBook,
  faTriangleExclamation,
  faFileLines,
  faFilePen,
  faStamp,
} from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';
import { ROUTES } from '@/routes/routes';
import { useUiStore } from '@/stores/ui.store';
import { useAuthStore } from '@/stores/auth.store';
import { useTenantBranding } from '@/hooks/use-tenant-branding';

const FIELD_ROLE_SLUGS = new Set(['service-engineer', 'master-service-engineer']);

const GROUPS_KEY = 'service-portal:sidebar-groups';

interface NavChild {
  label: string;
  to: string;
  icon: typeof faHouse;
  /** Backend permission string required to show this child. Absent = always visible. */
  permission?: string;
}

interface NavItem {
  label: string;
  to?: string;
  icon: typeof faHouse;
  /** Backend permission string required to show this leaf item. Absent = always visible. */
  permission?: string;
  children?: NavChild[];
}

/**
 * Reports → Report Templates (B6.9e), declared ONCE and shared by both
 * navigation sets below.
 *
 * It has to appear in both because the Master Service Engineer — the role that
 * owns the Report Designer — is a FIELD role and therefore reads
 * FIELD_NAV_ITEMS, while Owner and Admin read NAV_ITEMS. Repeating the literal
 * would let the two drift apart, which is precisely how a menu entry ends up
 * visible to one holder of a permission and not another.
 *
 * The gate is the existing B6.9a permission, consumed through the existing
 * `filterNavItems` mechanism — no second permission system, and no role-name
 * check. Service Engineer is a field role too and holds no `report-template:*`
 * permission (D-4), so the same filter hides it; Operations and Commercial are
 * hidden for the identical reason. Hiding is UX only: PermissionRoute guards the
 * routes and the backend PermissionGuard is the real boundary.
 */
const REPORTS_NAV_ITEM: NavItem = {
  label: 'Reports',
  icon: faFileLines,
  children: [
    {
      label: 'Report Templates',
      to: ROUTES.REPORT_TEMPLATES,
      icon: faFilePen,
      permission: 'report-template:read',
    },
  ],
};

/**
 * Field engineer navigation — shown for service-engineer and master-service-engineer.
 * Scoped to their workspace: My Inspections, Job Pool, Equipment, Customers.
 */
/**
 * Authorization Center — F-1, OD-A (approved 2026-08-25, option D1).
 *
 * `AUTHORIZATION_CASE_MANAGEMENT_DESIGN.md` §13 mandates this page as *"the
 * natural home for OPS/Commercial/Admin to find what's waiting on me"*. It was
 * built to that mandate, but nothing linked to it: the only reference outside
 * the router was the back button on its own detail page, so the queue was
 * reachable only by typing the URL. That is the defect this entry closes —
 * availability, never a security boundary.
 *
 * Declared ONCE and shared by both navigation arrays, exactly like
 * REPORTS_NAV_ITEM below and for the same reason: `inspection:authorization:read`
 * is held by ALL SIX roles, and Service Engineer / Master Service Engineer read
 * FIELD_NAV_ITEMS while Owner, Admin, Operations and Commercial read NAV_ITEMS.
 * Repeating the literal would let the two drift apart, which is precisely how a
 * menu entry ends up visible to one holder of a permission and not another.
 *
 * OD-A D1 extends §13's stated audience to SE/MSE deliberately: they CREATE the
 * cases, and with F-2 out of scope they would otherwise have no way to track one
 * except by reopening the originating finding.
 *
 * The gate is the existing permission, consumed through the existing
 * `filterNavItems` mechanism — no second permission system and no role-name
 * check. Hiding is UX only: PermissionRoute guards both routes and the backend
 * PermissionGuard is the real boundary.
 *
 * **No badge.** A count would need a producer, and none exists — that is F-2, a
 * separate decision. A hardcoded number here would be the very defect F-2
 * documents.
 *
 * Distinct from Master Data → "Approval Types" (`ApprovalRequestType`), which is
 * the catalog that CONFIGURES cases and never contains them. The label
 * "Authorizations" — not "Approvals" — keeps that boundary legible.
 */
const AUTHORIZATIONS_NAV_ITEM: NavItem = {
  label: 'Authorizations',
  to: ROUTES.AUTHORIZATION_CENTER,
  icon: faStamp,
  permission: 'inspection:authorization:read',
};

const FIELD_NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', to: ROUTES.ROOT, icon: faHouse },
  {
    label: 'My Inspections',
    to: ROUTES.MY_INSPECTIONS,
    icon: faClipboardList,
    permission: 'inspection:read',
  },
  {
    label: 'Job Pool',
    to: ROUTES.JOB_POOL,
    icon: faBoxOpen,
    permission: 'inspection:jobpool:view',
  },
  {
    label: 'Team Queue',
    to: ROUTES.TEAM_QUEUE,
    icon: faUserGroup,
    permission: 'inspection:unclaim',
  },
  AUTHORIZATIONS_NAV_ITEM,
  { label: 'Equipment', to: ROUTES.EQUIPMENT, icon: faGears, permission: 'equipment:view' },
  { label: 'Customers', to: ROUTES.CUSTOMERS, icon: faUsers, permission: 'customer:view' },
  {
    label: 'All Inspections',
    to: ROUTES.INSPECTIONS,
    icon: faClipboardCheck,
    permission: 'inspection:all:view',
  },
  {
    label: 'Procedures',
    icon: faBookOpen,
    children: [
      { label: 'Library', to: ROUTES.PROCEDURES, icon: faBook, permission: 'procedure:read' },
    ],
  },
  REPORTS_NAV_ITEM,
];

/**
 * Canonical navigation definition.
 * Each item/child declares the minimum permission needed to appear.
 * Groups are hidden when all their children are hidden.
 *
 * Permission strings must match the backend RBAC permission constants exactly.
 */
const NAV_ITEMS: NavItem[] = [
  // Dashboard is always visible — every authenticated role has a dashboard.
  { label: 'Dashboard', to: ROUTES.ROOT, icon: faHouse },

  {
    label: 'Organization',
    icon: faSitemap,
    children: [
      { label: 'Users', to: ROUTES.ORG_USERS, icon: faUserGroup, permission: 'user:view' },
      {
        label: 'Incomplete Identity',
        to: ROUTES.ORG_INCOMPLETE_IDENTITY,
        icon: faTriangleExclamation,
        permission: 'user:view',
      },
      {
        label: 'Departments',
        to: ROUTES.ORG_DEPARTMENTS,
        icon: faBuilding,
        permission: 'department:view',
      },
      { label: 'Roles', to: ROUTES.ORG_ROLES, icon: faShieldHalved, permission: 'role:view' },
    ],
  },

  { label: 'Customers', to: ROUTES.CUSTOMERS, icon: faUsers, permission: 'customer:view' },
  { label: 'Equipment', to: ROUTES.EQUIPMENT, icon: faGears, permission: 'equipment:view' },
  {
    label: 'Equipment Inward',
    to: ROUTES.EQUIPMENT_INTAKE,
    icon: faClipboardList,
    permission: 'intake:view',
  },
  {
    label: 'Receiving Lots',
    to: ROUTES.OPS_RECEIVING_LOTS,
    icon: faBoxesStacked,
    // Gated on :create (Owner/Admin/OPS only), not :view — Commercial holds
    // receiving-lot:view (Phase 1a, for future read access) but D-053 Phase 1b
    // explicitly does not add this workspace to Commercial's navigation.
    // The route itself still requires only :view, so this is a nav-visibility
    // choice, not the authorization boundary — PermissionGuard on the API and
    // PermissionRoute on the route are the real enforcement either way.
    permission: 'receiving-lot:create',
  },
  {
    label: 'Inspections',
    to: ROUTES.INSPECTIONS,
    icon: faClipboardCheck,
    permission: 'inspection:all:view',
  },

  AUTHORIZATIONS_NAV_ITEM,

  {
    label: 'Procedures',
    icon: faBookOpen,
    children: [
      {
        label: 'Library',
        to: ROUTES.PROCEDURES,
        icon: faBook,
        permission: 'procedure:read',
      },
    ],
  },

  REPORTS_NAV_ITEM,

  {
    label: 'Master Data',
    icon: faLayerGroup,
    children: [
      {
        label: 'Manufacturers',
        to: ROUTES.MANUFACTURERS,
        icon: faIndustry,
        permission: 'manufacturer:create',
      },
      {
        label: 'Categories',
        to: ROUTES.EQUIPMENT_CATEGORIES,
        icon: faTag,
        permission: 'category:create',
      },
      {
        label: 'Equipment Types',
        to: ROUTES.EQUIPMENT_TYPES,
        icon: faCubesStacked,
        permission: 'equipment-type:create',
      },
      {
        label: 'Models',
        to: ROUTES.EQUIPMENT_MODELS,
        icon: faCubesStacked,
        permission: 'model:create',
      },
      {
        label: 'Accessories',
        to: ROUTES.ACCESSORIES,
        icon: faScrewdriverWrench,
        permission: 'accessory:create',
      },
      {
        label: 'Service Tasks',
        to: ROUTES.SERVICE_TASKS,
        icon: faListCheck,
        permission: 'service-task:create',
      },
      {
        label: 'Inspection Parameters',
        to: ROUTES.INSPECTION_PARAMETERS,
        icon: faListCheck,
        // Owner decision D2 — the configuration is a property of the equipment
        // model, so it reuses the model permissions rather than inventing a pair.
        permission: 'model:update',
      },
      {
        label: 'Service Types',
        to: ROUTES.SERVICE_TYPES,
        icon: faScrewdriverWrench,
        permission: 'service-type:create',
      },
      {
        label: 'Warranty Types',
        to: ROUTES.WARRANTY_TYPES,
        icon: faShield,
        permission: 'warranty-type:create',
      },
      { label: 'Priorities', to: ROUTES.PRIORITIES, icon: faFlag, permission: 'priority:create' },
      {
        label: 'Approval Types',
        to: ROUTES.APPROVAL_REQUEST_TYPES,
        icon: faClipboardCheck,
        permission: 'approval-request-type:create',
      },
    ],
  },

  {
    label: 'Settings',
    icon: faGear,
    children: [
      {
        label: 'Company Profile',
        to: ROUTES.SETTINGS_COMPANY_PROFILE,
        icon: faBuilding,
        permission: 'company-profile:view',
      },
    ],
  },
];

/** Returns a filtered copy of NAV_ITEMS keeping only items the user may see. */
function filterNavItems(items: NavItem[], can: (perm: string) => boolean): NavItem[] {
  return items.reduce<NavItem[]>((acc, item) => {
    if (item.children !== undefined) {
      const visibleChildren = item.children.filter(
        (c) => c.permission === undefined || can(c.permission),
      );
      if (visibleChildren.length > 0) {
        acc.push({ ...item, children: visibleChildren });
      }
    } else {
      if (item.permission === undefined || can(item.permission)) {
        acc.push(item);
      }
    }
    return acc;
  }, []);
}

function getInitialExpanded(): Set<string> {
  try {
    const raw = localStorage.getItem(GROUPS_KEY);
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch {
    // corrupted storage — start fresh
  }
  return new Set();
}

// Shared base text colors on the sidebar surface
const INACTIVE = 'text-foreground/70';
const INACTIVE_HOVER = 'hover:bg-primary/[0.14] hover:text-foreground';
const ACTIVE_BG = 'bg-primary/[0.22]';
const ACTIVE_TEXT = 'text-primary font-bold';

export function Sidebar() {
  const { brandName, logoUrl } = useTenantBranding();
  // Reset when the tenant (and therefore the logo) changes, so a previously
  // failed load does not permanently suppress a different tenant's logo.
  const [logoFailed, setLogoFailed] = useState(false);
  useEffect(() => setLogoFailed(false), [logoUrl]);
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);
  const isMobileSidebarOpen = useUiStore((s) => s.isMobileSidebarOpen);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const setMobileSidebarOpen = useUiStore((s) => s.setMobileSidebarOpen);
  const { pathname } = useLocation();

  const userPermissions = useAuthStore((s) => s.user?.permissions ?? []);
  const roleSlug = useAuthStore((s) => s.user?.roleSlug ?? null);
  const isOwner = userPermissions.includes('*');
  const isFieldRole = roleSlug !== null && FIELD_ROLE_SLUGS.has(roleSlug);
  const can = useMemo(
    () =>
      (perm: string): boolean =>
        isOwner || userPermissions.includes(perm),
    [isOwner, userPermissions],
  );

  const sourceItems = isFieldRole ? FIELD_NAV_ITEMS : NAV_ITEMS;
  const visibleNavItems = useMemo(() => filterNavItems(sourceItems, can), [sourceItems, can]);

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(getInitialExpanded);

  const toggleGroup = (label: string) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      try {
        localStorage.setItem(GROUPS_KEY, JSON.stringify([...next]));
      } catch {
        // ignore quota errors
      }
      return next;
    });

  /** Top-level NavLink className factory */
  const topLinkCls = ({ isActive }: { isActive: boolean }) => {
    if (sidebarCollapsed) {
      return cn(
        'flex w-full justify-center items-center rounded-lg px-2 py-2 text-sm transition-all duration-150',
        isActive ? cn(ACTIVE_BG, ACTIVE_TEXT) : cn(INACTIVE, INACTIVE_HOVER),
      );
    }
    return cn(
      'flex items-center gap-3 w-full rounded-lg text-sm transition-all duration-150',
      isActive
        ? cn('border-l-[4px] border-primary pl-[8px] pr-3 py-2', ACTIVE_BG, ACTIVE_TEXT)
        : cn('px-3 py-2', INACTIVE, INACTIVE_HOVER),
    );
  };

  /** Child NavLink className factory */
  const childLinkCls = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex items-center gap-2.5 w-full rounded-lg px-3 py-1.5 text-sm transition-all duration-150',
      isActive ? cn('bg-primary/[0.18] text-primary font-semibold') : cn(INACTIVE, INACTIVE_HOVER),
    );

  return (
    <>
      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex h-screen flex-col border-r border-sidebar-border bg-sidebar transition-all duration-300 lg:relative lg:z-auto',
          sidebarCollapsed ? 'w-16' : 'w-64',
          isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        {/* Brand */}
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-sidebar-border px-3">
          {!sidebarCollapsed && (
            <span className="flex min-w-0 items-center gap-2.5">
              {logoUrl && !logoFailed && (
                // Auto-scaling: the logo is given a fixed height and an
                // unconstrained width up to a cap, so wide wordmarks and square
                // icons both render at a sensible size without distortion
                // (object-contain preserves aspect ratio).
                <img
                  src={logoUrl}
                  alt=""
                  className="h-9 w-auto max-w-[7.5rem] shrink-0 object-contain object-left"
                  // Hide gracefully: a broken or removed logo URL must not leave
                  // a torn-image icon in the shell. React state rather than
                  // inline style, so the layout reflows and the gap collapses.
                  onError={() => setLogoFailed(true)}
                />
              )}
              <span
                className="truncate text-sm font-bold tracking-tight text-foreground"
                title={brandName}
              >
                {brandName}
              </span>
            </span>
          )}
          <button
            onClick={toggleSidebar}
            className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors',
              INACTIVE,
              INACTIVE_HOVER,
              sidebarCollapsed && 'mx-auto',
            )}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <FontAwesomeIcon
              icon={sidebarCollapsed ? faChevronRight : faChevronLeft}
              className="h-3.5 w-3.5"
            />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5" aria-label="Main navigation">
          {visibleNavItems.map((item) => {
            if (item.children) {
              const isExpanded = expandedGroups.has(item.label);
              const hasActiveChild = item.children.some(
                (c) => pathname === c.to || pathname.startsWith(c.to + '/'),
              );

              return (
                <div key={item.label} className="space-y-0.5">
                  <button
                    onClick={() => !sidebarCollapsed && toggleGroup(item.label)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150',
                      hasActiveChild ? 'text-primary font-semibold' : cn(INACTIVE, INACTIVE_HOVER),
                      sidebarCollapsed && 'justify-center',
                    )}
                    aria-expanded={isExpanded}
                    title={sidebarCollapsed ? item.label : undefined}
                  >
                    <FontAwesomeIcon icon={item.icon} className="h-4 w-4 shrink-0" />
                    {!sidebarCollapsed && (
                      <>
                        <span className="flex-1 text-left">{item.label}</span>
                        <FontAwesomeIcon
                          icon={isExpanded ? faChevronUp : faChevronDown}
                          className="h-3 w-3 opacity-50"
                        />
                      </>
                    )}
                  </button>
                  {!sidebarCollapsed && isExpanded && (
                    <div className="ml-3 mt-0.5 space-y-0.5 border-l-2 border-sidebar-border pl-2">
                      {item.children.map((child) => (
                        <NavLink key={child.to} to={child.to} className={childLinkCls}>
                          <FontAwesomeIcon icon={child.icon} className="h-3.5 w-3.5 shrink-0" />
                          <span>{child.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <NavLink
                key={item.to}
                to={item.to!}
                end={item.to === ROUTES.ROOT}
                className={topLinkCls}
                aria-label={sidebarCollapsed ? item.label : undefined}
                title={sidebarCollapsed ? item.label : undefined}
              >
                <FontAwesomeIcon icon={item.icon} className="h-4 w-4 shrink-0" />
                {!sidebarCollapsed && <span>{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
